package br.gov.go.pge.permanenciaweb.site.components

import br.gov.go.pge.permanenciaweb.shared.OccupantDto

/*
 * Roster alert vocabulary.
 *
 * The wire carries stable codes (`INCONSISTENTE`, `LIBERADO`) and the UI owns the Portuguese
 * labels, so renaming what an officer reads never touches the API. A row can carry more than one
 * alert: an inconsistent day *and* an active after-hours permit are independent facts.
 */

internal const val ALERTA_INCONSISTENTE = "Inconsistente"
internal const val ALERTA_LIBERADO = "Liberado"
internal const val ALERTA_EXPIRADO = "Expirado"
internal const val ALERTA_SEM_SAIDA = "Sem saída"

/** Plain-language explanation for the "Inconsistente" chip (`dia_pessoa_inconsistente`). */
internal const val ALERTA_INCONSISTENTE_TOOLTIP =
    "Marcações conflitantes: o dia começa com uma saída ou termina com uma entrada."

internal fun alertLabel(code: String): String = when (code) {
    "INCONSISTENTE" -> ALERTA_INCONSISTENTE
    "LIBERADO" -> ALERTA_LIBERADO
    "EXPIRADO" -> ALERTA_EXPIRADO
    "SEM_SAIDA" -> ALERTA_SEM_SAIDA
    else -> code
}

/** Labels of the row's alerts, in wire order (inconsistência first). */
internal val OccupantDto.alertasLabels: List<String>
    get() = alertas.map(::alertLabel)

/** Same condition as `Counts.inconsistentes` on the server. */
internal fun OccupantDto.inconsistente(): Boolean = "INCONSISTENTE" in alertas

/** Valid now or later today: the person may stay. */
internal fun OccupantDto.liberado(): Boolean = "LIBERADO" in alertas

/** Today's permit already ended: shown as expired and counted as "sem liberação". */
internal fun OccupantDto.expirado(): Boolean = "EXPIRADO" in alertas

/** Has non-permitted closes in the rolling 30-day window. */
internal fun OccupantDto.semSaida(): Boolean = "SEM_SAIDA" in alertas

/** `19:00–07:00`, when the row carries a window. */
internal fun OccupantDto.intervaloLiberacao(): String? =
    if (liberadoDe != null && liberadoAte != null) "$liberadoDe–$liberadoAte" else null

/**
 * Text the "Alerta" column sorts and filters on: labels with the window, so typing "19:00" finds
 * the row. The checkbox facets use [alertasLabels] (label only) as tokens.
 */
internal fun OccupantDto.alertasTexto(): String = alertasLabels.joinToString(", ") { label ->
    when {
        label == ALERTA_SEM_SAIDA -> diasSemSaida30?.let { "$label: ${it}d" } ?: label
        label == ALERTA_LIBERADO || label == ALERTA_EXPIRADO -> {
            val intervalo = intervaloLiberacao()
            if (intervalo != null) "$label $intervalo" else label
        }
        else -> label
    }
}
